/**
 * Application Stage Constants — Single Source of Truth (Frontend)
 * Epic 3B: 52-stage broker application workflow
 */

// ── Stage Display Names ──────────────────────────────────────────────────────

export const STAGE_DISPLAY: Record<string, string> = {
  new_lead:                          "New Lead",
  initial_consultation_scheduled:    "Consultation Scheduled",
  initial_consultation_done:         "Initial Consultation Done",
  fact_find_in_progress:             "Fact Find",
  loan_research_in_progress:         "Researching Products",
  recommendation_prepared:           "Recommendation Prepared",
  recommendation_presented:          "Recommendation Presented",
  recommendation_accepted:           "Recommendation Accepted",
  documents_checklist_sent:          "Checklist Sent",
  awaiting_documents:                "Awaiting Documents",
  documents_partially_received:      "Docs Partially Received",
  documents_in_review:               "Documents in Review",
  documents_incomplete:              "Documents Incomplete",
  documents_complete:                "Documents Complete",
  application_in_preparation:        "Preparing Application",
  application_sent_for_review:       "Sent for Review",
  application_signed:                "Application Signed",
  application_submitted:             "Submitted to Lender",
  application_acknowledged:          "Lender Acknowledged",
  pending_additional_information:    "Lender Requesting Info",
  credit_assessment_in_progress:     "Credit Assessment",
  pending_identity_verification:     "Pending ID Verification",
  pending_income_verification:       "Pending Income Verification",
  pending_employment_verification:   "Pending Employment Verification",
  pending_bank_verification:         "Pending Bank Verification",
  valuation_ordered:                 "Valuation Ordered",
  valuation_in_progress:             "Valuation In Progress",
  valuation_received:                "Valuation Received",
  valuation_shortfall:               "Valuation Shortfall",
  revaluation_required:              "Re-valuation Required",
  revaluation_in_progress:           "Re-valuation In Progress",
  revaluation_received:              "Re-valuation Received",
  lmi_assessment_required:           "LMI Assessment Required",
  lmi_approved:                      "LMI Approved",
  lmi_declined:                      "LMI Declined",
  pre_approval_in_progress:          "Pre-Approval In Progress",
  pre_approval_obtained:             "Pre-Approval Obtained",
  pre_approval_declined:             "Pre-Approval Declined",
  pre_approval_expired:              "Pre-Approval Expired",
  conditional_approval_in_progress:  "Conditional Approval In Progress",
  conditional_approval_obtained:     "Conditional Approval Obtained",
  conditions_being_cleared:          "Clearing Conditions",
  conditions_cleared:                "All Conditions Cleared",
  unconditional_approval_obtained:   "Unconditional Approval",
  letter_of_offer_issued:            "Letter of Offer Issued",
  letter_of_offer_accepted:          "Letter of Offer Accepted",
  loan_documents_issued:             "Loan Documents Issued",
  awaiting_loan_document_signing:    "Awaiting Doc Signing",
  loan_documents_signed:             "Loan Docs Signed",
  settlement_date_being_arranged:    "Arranging Settlement",
  settlement_scheduled:              "Settlement Scheduled",
  settlement_in_progress:            "Settlement In Progress",
  settled:                           "Settled",
  declined:                          "Declined",
  withdrawn_by_client:               "Withdrawn by Client",
  withdrawn_by_broker:               "Withdrawn by Broker",
  lapsed:                            "Lapsed",
  on_hold:                           "On Hold",
}

export const ALL_STAGES = Object.keys(STAGE_DISPLAY)

// ── Kanban Board Columns ─────────────────────────────────────────────────────

export interface KanbanColumn {
  id: string
  label: string
  stages: string[]
}

export const KANBAN_COLUMNS: KanbanColumn[] = [
  { id: "discovery",   label: "Discovery",   stages: ["new_lead","initial_consultation_scheduled","initial_consultation_done","fact_find_in_progress"] },
  { id: "research",    label: "Research",     stages: ["loan_research_in_progress","recommendation_prepared","recommendation_presented","recommendation_accepted"] },
  { id: "docs",        label: "Documents",    stages: ["documents_checklist_sent","awaiting_documents","documents_partially_received","documents_in_review","documents_incomplete","documents_complete"] },
  { id: "submission",  label: "Submission",   stages: ["application_in_preparation","application_sent_for_review","application_signed","application_submitted","application_acknowledged","pending_additional_information"] },
  { id: "assessment",  label: "Assessment",   stages: ["credit_assessment_in_progress","pending_identity_verification","pending_income_verification","pending_employment_verification","pending_bank_verification","valuation_ordered","valuation_in_progress","valuation_received","valuation_shortfall","revaluation_required","revaluation_in_progress","revaluation_received","lmi_assessment_required","lmi_approved","lmi_declined"] },
  { id: "approval",    label: "Approval",     stages: ["pre_approval_in_progress","pre_approval_obtained","pre_approval_declined","pre_approval_expired","conditional_approval_in_progress","conditional_approval_obtained","conditions_being_cleared","conditions_cleared","unconditional_approval_obtained","letter_of_offer_issued","letter_of_offer_accepted"] },
  { id: "settlement",  label: "Settlement",   stages: ["loan_documents_issued","awaiting_loan_document_signing","loan_documents_signed","settlement_date_being_arranged","settlement_scheduled","settlement_in_progress","settled"] },
  { id: "on_hold",     label: "On Hold",      stages: ["on_hold"] },
  { id: "closed",      label: "Closed",       stages: ["declined","withdrawn_by_client","withdrawn_by_broker","lapsed"] },
]

// ── Stage Transition Rules (mirrors backend VALID_TRANSITIONS) ───────────────
// Mutable: updated at boot via fetchStageConfig()

export let STAGE_TRANSITIONS: Record<string, string[]> = {
  new_lead: ["initial_consultation_scheduled", "initial_consultation_done"],
  initial_consultation_scheduled: ["initial_consultation_done"],
  initial_consultation_done: ["fact_find_in_progress"],
  fact_find_in_progress: ["loan_research_in_progress"],
  loan_research_in_progress: ["recommendation_prepared"],
  recommendation_prepared: ["recommendation_presented"],
  recommendation_presented: ["recommendation_accepted", "loan_research_in_progress"],
  recommendation_accepted: ["documents_checklist_sent"],
  documents_checklist_sent: ["awaiting_documents"],
  awaiting_documents: ["documents_partially_received"],
  documents_partially_received: ["documents_in_review"],
  documents_in_review: ["documents_complete", "documents_incomplete"],
  documents_incomplete: ["awaiting_documents"],
  documents_complete: ["application_in_preparation"],
  application_in_preparation: ["application_sent_for_review"],
  application_sent_for_review: ["application_signed"],
  application_signed: ["application_submitted"],
  application_submitted: ["application_acknowledged"],
  application_acknowledged: ["credit_assessment_in_progress", "pending_additional_information", "pre_approval_in_progress"],
  pending_additional_information: ["application_acknowledged"],
  credit_assessment_in_progress: ["pending_identity_verification", "pending_income_verification", "pending_employment_verification", "pending_bank_verification", "valuation_ordered", "lmi_assessment_required", "conditional_approval_in_progress", "pre_approval_in_progress"],
  pending_identity_verification: ["credit_assessment_in_progress"],
  pending_income_verification: ["credit_assessment_in_progress"],
  pending_employment_verification: ["credit_assessment_in_progress"],
  pending_bank_verification: ["credit_assessment_in_progress"],
  valuation_ordered: ["valuation_in_progress"],
  valuation_in_progress: ["valuation_received"],
  valuation_received: ["credit_assessment_in_progress", "valuation_shortfall"],
  valuation_shortfall: ["revaluation_required"],
  revaluation_required: ["revaluation_in_progress"],
  revaluation_in_progress: ["revaluation_received"],
  revaluation_received: ["credit_assessment_in_progress"],
  lmi_assessment_required: ["lmi_approved", "lmi_declined"],
  lmi_approved: ["credit_assessment_in_progress"],
  lmi_declined: ["loan_research_in_progress"],
  pre_approval_in_progress: ["pre_approval_obtained", "pre_approval_declined"],
  pre_approval_declined: ["loan_research_in_progress"],
  pre_approval_obtained: ["conditional_approval_in_progress", "pre_approval_expired"],
  pre_approval_expired: ["pre_approval_in_progress"],
  conditional_approval_in_progress: ["conditional_approval_obtained"],
  conditional_approval_obtained: ["conditions_being_cleared"],
  conditions_being_cleared: ["conditions_cleared"],
  conditions_cleared: ["unconditional_approval_obtained"],
  unconditional_approval_obtained: ["letter_of_offer_issued"],
  letter_of_offer_issued: ["letter_of_offer_accepted"],
  letter_of_offer_accepted: ["loan_documents_issued"],
  loan_documents_issued: ["awaiting_loan_document_signing"],
  awaiting_loan_document_signing: ["loan_documents_signed"],
  loan_documents_signed: ["settlement_date_being_arranged"],
  settlement_date_being_arranged: ["settlement_scheduled"],
  settlement_scheduled: ["settlement_in_progress"],
  settlement_in_progress: ["settled"],
}

export let UNIVERSAL_TERMINALS = ["on_hold", "withdrawn_by_client", "withdrawn_by_broker", "lapsed"]
export let DECLINED_FROM = ["credit_assessment_in_progress", "conditional_approval_in_progress"]
export let TERMINAL_STAGES = ["settled", "declined", "withdrawn_by_client", "withdrawn_by_broker", "lapsed"]

// All non-terminal stages (for on_hold resume — broker picks where to resume)
const ALL_ACTIVE_STAGES = Object.keys(STAGE_DISPLAY).filter(
  s => !["settled", "declined", "withdrawn_by_client", "withdrawn_by_broker", "lapsed", "on_hold"].includes(s)
)

export function getValidNextStages(currentStage: string): string[] {
  if (TERMINAL_STAGES.includes(currentStage)) return []
  // On hold: can resume to any active stage OR close out (declined, withdrawn, lapsed)
  if (currentStage === "on_hold") return [...ALL_ACTIVE_STAGES, "declined", "withdrawn_by_client", "withdrawn_by_broker", "lapsed"]
  const explicit = STAGE_TRANSITIONS[currentStage] || []
  const terminals = [...UNIVERSAL_TERMINALS]
  if (DECLINED_FROM.includes(currentStage)) terminals.push("declined")
  return [...explicit, ...terminals]
}

// ── Phase Colors ─────────────────────────────────────────────────────────────

export const PHASE_COLORS: Record<string, string> = {
  discovery:  "#3B82F6", // blue
  research:   "#8B5CF6", // purple
  docs:       "#F59E0B", // amber
  submission: "#EF4444", // red
  assessment: "#EC4899", // pink
  approval:   "#10B981", // emerald
  settlement: "#06B6D4", // cyan
  on_hold:    "#F59E0B", // amber
  closed:     "#6B7280", // gray
}

export function getPhaseForStage(stage: string): string {
  for (const col of KANBAN_COLUMNS) {
    if (col.stages.includes(stage)) return col.id
  }
  return "closed"
}

export function getPhaseColor(stage: string): string {
  return PHASE_COLORS[getPhaseForStage(stage)] || PHASE_COLORS.closed
}

// ── SLA Thresholds (days) ────────────────────────────────────────────────────

export let STAGE_SLA: Record<string, number> = {
  new_lead: 1,
  initial_consultation_scheduled: 3,
  awaiting_documents: 5,
  documents_partially_received: 3,
  documents_in_review: 2,
  application_in_preparation: 3,
  application_submitted: 15,
  credit_assessment_in_progress: 10,
  valuation_ordered: 5,
  pending_additional_information: 2,
  conditional_approval_obtained: 5,
  loan_documents_issued: 3,
  awaiting_loan_document_signing: 3,
  settlement_date_being_arranged: 5,
}

export function getSlaStatus(stage: string, daysInStage: number): 'green' | 'amber' | 'red' {
  const sla = STAGE_SLA[stage]
  if (!sla) return 'green'
  if (daysInStage > sla) return 'red'
  if (daysInStage > sla * 0.75) return 'amber'
  return 'green'
}

// ── Customer-facing Phase Progress ───────────────────────────────────────────

export const CUSTOMER_PHASES = [
  { id: "discovery",  label: "Getting Started",  icon: "search" },
  { id: "research",   label: "Finding Options",   icon: "lightbulb" },
  { id: "docs",       label: "Documents",          icon: "file-text" },
  { id: "submission", label: "Submitted",          icon: "send" },
  { id: "assessment", label: "Under Review",       icon: "clipboard" },
  { id: "approval",   label: "Approved",           icon: "check-circle" },
  { id: "settlement", label: "Settlement",         icon: "home" },
  { id: "closed",     label: "Complete",            icon: "flag" },
]

export function getPhaseIndex(stage: string): number {
  const phase = getPhaseForStage(stage)
  return CUSTOMER_PHASES.findIndex(p => p.id === phase)
}

// ── Fetch Stage Config from Backend ─────────────────────────────────────────

import { authenticatedFetch } from '../api/authApi'

const API_BASE = import.meta.env.VITE_API_URL || ''

export async function fetchStageConfig(token: string): Promise<void> {
  try {
    const resp = await authenticatedFetch(`${API_BASE}/api/applications/stage-config`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    if (!resp.ok) return // Keep defaults on failure
    const config = await resp.json()
    if (config.transitions) STAGE_TRANSITIONS = config.transitions
    if (config.universalTerminals) UNIVERSAL_TERMINALS = config.universalTerminals
    if (config.declinedFrom) DECLINED_FROM = config.declinedFrom
    if (config.terminalStages) TERMINAL_STAGES = config.terminalStages
    if (config.sla) STAGE_SLA = config.sla
  } catch {
    // Network error — keep hardcoded defaults
  }
}
